import { useCallback, useMemo } from 'react';
import type { GlobalGrid, PlanCell } from '../../../types/plan';
import type { PlanningSelectionInfo } from '../../../types/planManagement';
import type { EntityLookup } from '../../../utils/planEntities';
import { isBedEntity } from '../../../utils/planEntities';
import { planCellKey } from '../../../utils/planCells';
import type { GridSelectionRect } from './types';

const adjacentCoordinate = (value: number, delta: -1 | 1): number => {
  if (value === 1 && delta === -1) return -1;
  if (value === -1 && delta === 1) return 1;
  return value + delta;
};

export const buildSelectedStructuralBoundaryCrossingCellKeys = (
  cellMap: Map<string, PlanCell>,
  selectedCellKeys: Set<string>,
): Set<string> => {
  const crossingCellKeys = new Set<string>();

  selectedCellKeys.forEach((key) => {
    const [rowStr, colStr] = key.split(':');
    const row = Number(rowStr);
    const col = Number(colStr);
    const structuralObjectId = cellMap.get(key)?.structuralObjectId ?? null;

    const adjacentKeys = [
      planCellKey(adjacentCoordinate(row, -1), col),
      planCellKey(adjacentCoordinate(row, 1), col),
      planCellKey(row, adjacentCoordinate(col, -1)),
      planCellKey(row, adjacentCoordinate(col, 1)),
    ];

    adjacentKeys.forEach((adjacentKey) => {
      if (!selectedCellKeys.has(adjacentKey)) return;
      const adjacentStructuralObjectId = cellMap.get(adjacentKey)?.structuralObjectId ?? null;
      if (adjacentStructuralObjectId === structuralObjectId) return;
      crossingCellKeys.add(key);
      crossingCellKeys.add(adjacentKey);
    });
  });

  return crossingCellKeys;
};

interface UsePlanningSelectionParams {
  selectionStart: { row: number; col: number } | null;
  selectionEnd: { row: number; col: number } | null;
  cellMap: Map<string, PlanCell>;
  globalGrid: GlobalGrid;
  entityLookup: EntityLookup;
  bedsWithPlantings: Set<string>;
}

export const usePlanningSelection = ({
  selectionStart,
  selectionEnd,
  cellMap,
  globalGrid,
  entityLookup,
  bedsWithPlantings,
}: UsePlanningSelectionParams) => {
  const selectionRect = useMemo<GridSelectionRect | null>(() => {
    if (!selectionStart || !selectionEnd) return null;

    const minRow = Math.min(selectionStart.row, selectionEnd.row);
    const maxRow = Math.max(selectionStart.row, selectionEnd.row);
    const minCol = Math.min(selectionStart.col, selectionEnd.col);
    const maxCol = Math.max(selectionStart.col, selectionEnd.col);

    let rows = maxRow - minRow + 1;
    let cols = maxCol - minCol + 1;
    if (minRow < 0 && maxRow > 0) rows -= 1;
    if (minCol < 0 && maxCol > 0) cols -= 1;

    const maxBoundingBox = globalGrid.maxBoundingBox || 32;
    const exceedsBoundary = rows > maxBoundingBox || cols > maxBoundingBox;

    const selectedCellKeys = new Set<string>();
    for (let row = minRow; row <= maxRow; row += 1) {
      if (row === 0) continue;
      for (let col = minCol; col <= maxCol; col += 1) {
        if (col === 0) continue;
        selectedCellKeys.add(planCellKey(row, col));
      }
    }
    const crossingCellKeys = buildSelectedStructuralBoundaryCrossingCellKeys(cellMap, selectedCellKeys);

    const occupiedCells: string[] = [];
    for (let row = minRow; row <= maxRow; row += 1) {
      if (row === 0) continue;
      for (let col = minCol; col <= maxCol; col += 1) {
        if (col === 0) continue;
        const key = planCellKey(row, col);
        const cell = cellMap.get(key);
        if ((cell && (cell.kind as string) !== 'empty') || crossingCellKeys.has(key)) {
          occupiedCells.push(key);
        }
      }
    }

    return { minRow, maxRow, minCol, maxCol, rows, cols, occupiedCells, exceedsBoundary, maxBoundingBox };
  }, [selectionStart, selectionEnd, cellMap, globalGrid]);

  const analyzePlanningSelection = useCallback((
    minRow: number,
    maxRow: number,
    minCol: number,
    maxCol: number,
  ): PlanningSelectionInfo => {
    const selectedCellKeys = new Set<string>();
    let emptyCellCount = 0;
    let pathCellCount = 0;
    let obstacleCellCount = 0;
    let structuralObjectCellCount = 0;
    const structuralObjectIds = new Set<string>();
    const bedEntities = new Map<string, { bedId: string; hasPlanting: boolean; cellCount: number; entity: any }>();

    for (let row = minRow; row <= maxRow; row += 1) {
      if (row === 0) continue;
      for (let col = minCol; col <= maxCol; col += 1) {
        if (col === 0) continue;
        const key = planCellKey(row, col);
        const cell = cellMap.get(key);
        const entity = entityLookup.get(key);

        if (!cell || (cell.kind as string) === 'empty') {
          selectedCellKeys.add(key);
          if (!cell?.structuralObjectId) emptyCellCount += 1;
        } else if (entity && isBedEntity(entity)) {
          if (!bedEntities.has(entity.bedId)) {
            bedEntities.set(entity.bedId, {
              bedId: entity.bedId,
              hasPlanting: bedsWithPlantings.has(entity.bedId),
              cellCount: entity.cells.length,
              entity,
            });
          }
        } else if (cell.kind === 'path') {
          selectedCellKeys.add(key);
          pathCellCount += 1;
        } else if (cell.kind === 'obstacle') {
          selectedCellKeys.add(key);
          obstacleCellCount += 1;
        }

        if (cell?.structuralObjectId) {
          structuralObjectCellCount += 1;
          structuralObjectIds.add(cell.structuralObjectId);
        }
      }
    }

    bedEntities.forEach(({ entity }) => {
      if (isBedEntity(entity)) {
        entity.cells.forEach(({ row, col }: { row: number; col: number }) => selectedCellKeys.add(planCellKey(row, col)));
      }
    });

    const beds = Array.from(bedEntities.values()).map(({ bedId, hasPlanting, cellCount }) => ({ bedId, hasPlanting, cellCount }));
    const structuralObjectBoundaryCrossingCellCount = buildSelectedStructuralBoundaryCrossingCellKeys(
      cellMap,
      selectedCellKeys,
    ).size;

    return {
      selectedCellKeys,
      rectMinRow: minRow,
      rectMaxRow: maxRow,
      rectMinCol: minCol,
      rectMaxCol: maxCol,
      emptyCellCount,
      pathCellCount,
      obstacleCellCount,
      structuralObjectCellCount,
      structuralObjectBoundaryCrossingCellCount,
      structuralObjectIds: Array.from(structuralObjectIds),
      beds,
      hasPlantedBeds: beds.some((b) => b.hasPlanting),
      allEmpty: beds.length === 0
        && pathCellCount === 0
        && obstacleCellCount === 0
        && structuralObjectIds.size === 0,
      hasEntities: beds.length > 0
        || pathCellCount > 0
        || obstacleCellCount > 0
        || structuralObjectIds.size > 0,
      totalCells: selectedCellKeys.size,
    };
  }, [cellMap, entityLookup, bedsWithPlantings]);

  return { selectionRect, analyzePlanningSelection };
};
